/**
| 属性                    | 必填 | 类型      | 可选值 / 示例            | 作用                              |
| --------------------- | -: | ------- | ------------------- | ------------------------------- |
| `class`               |  是 | string  | `affirm-as-low-as`  | 声明这是 Affirm 分期宣传组件              |
| `data-amount`         |  是 | integer | `129900`            | 商品或购物车金额，单位为最小货币单位；美国场景下即 cents |
| `data-page-type`      |  是 | string  | `product`、`cart` 等  | 告诉 Affirm 当前组件所在的页面类型           |
| `data-promo-id`       |  否 | string  | Affirm 提供的 Promo ID | 指定特定营销模板或融资计划                   |
| `data-affirm-type`    |  否 | string  | `text`、`symbol`     | 控制 Affirm 标志的展示形式               |
| `data-affirm-color`   |  否 | string  | `black`、`white`     | 控制 Affirm 标志颜色                  |
| `data-category`       |  否 | string  | `electronics`       | 商品分类，主要用于分析追踪                   |
| `data-brand`          |  否 | string  | `Samsung`           | 商品品牌，主要用于分析追踪                   |
| `data-sku`            |  否 | string  | `ABC123`            | 商品 SKU，主要用于分析追踪                 |
| `data-learnmore-show` |  否 | string  | `true`、`false`      | 控制 “Learn more” 链接的展示方式         |
*/
const TAG_NAME = "affirm-placement";

const SCRIPT_URLS = {
  production: "https://cdn1.affirm.com/js/v2/affirm.js",
  sandbox: "https://cdn1-sandbox.affirm.com/js/v2/affirm.js",
};

const ATTRIBUTE_MAP = {
  "page-type": "data-page-type",
  "promo-id": "data-promo-id",
  "affirm-type": "data-affirm-type",
  "affirm-color": "data-affirm-color",
  category: "data-category",
  brand: "data-brand",
  sku: "data-sku",
  "learn-more": "data-learnmore-show",
};

let affirmLoadPromise = null;
let refreshFrame = null;

function isAffirmReady() {
  return Boolean(
    window.affirm?.ui
    && typeof window.affirm.ui.ready === "function"
    && typeof window.affirm.ui.refresh === "function",
  );
}

function findAffirmScript() {
  return Array.from(document.scripts).find((script) => {
    return script.src.includes("/js/v2/affirm.js");
  });
}

function bootstrapAffirm({ publicApiKey, scriptUrl, locale, countryCode }) {
  window._affirm_config = {
    public_api_key: publicApiKey,
    script: scriptUrl,
    locale,
    country_code: countryCode,
  };

  (function (
    root,
    config,
    affirmName,
    checkoutName,
    uiName,
    scriptName,
    readyName,
    jsReadyName,
  ) {
    const affirm = root[affirmName] || {};

    const queueMethod = (object, property, command) => {
      return function () {
        object[property]._.push([command, arguments]);
      };
    };

    affirm[checkoutName] = queueMethod(affirm, checkoutName, "set");

    const checkout = affirm[checkoutName];

    affirm[uiName] = {};
    affirm[uiName]._ = [];
    checkout._ = [];
    affirm._ = [];

    affirm[uiName][readyName] = queueMethod(affirm, uiName, readyName);

    affirm[jsReadyName] = function () {
      affirm._.push([readyName, arguments]);
    };

    [
      "set",
      "add",
      "save",
      "post",
      "open",
      "empty",
      "reset",
      "on",
      "off",
      "trigger",
      "ready",
      "setProduct",
    ].forEach((method) => {
      checkout[method] = queueMethod(affirm, checkoutName, method);
    });

    ["get", "token", "url", "items"].forEach((method) => {
      checkout[method] = function () {};
    });

    const script = document.createElement(scriptName);

    script.async = true;
    script.src = config[scriptName];
    script.dataset.affirmPlacementLoader = "";

    document.head.appendChild(script);

    delete config[scriptName];

    checkout(config);
    root[affirmName] = affirm;
  })(
    window,
    window._affirm_config,
    "affirm",
    "checkout",
    "ui",
    "script",
    "ready",
    "jsReady",
  );
}

function waitForAffirm(timeout = 15_000) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    function check() {
      if (isAffirmReady()) {
        resolve(window.affirm);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        reject(
          new Error("[affirm-placement] Affirm initialization timed out."),
        );
        return;
      }

      window.setTimeout(check, 50);
    }

    check();
  });
}

function loadAffirm(options) {
  if (isAffirmReady()) {
    return Promise.resolve(window.affirm);
  }

  if (affirmLoadPromise) {
    return affirmLoadPromise;
  }

  affirmLoadPromise = new Promise((resolve, reject) => {
    const existingScript = findAffirmScript();

    /*
     * Affirm 已由其他代码开始加载时，只等待，不重复初始化。
     */
    if (window.affirm || existingScript) {
      waitForAffirm(options.timeout).then(resolve).catch(reject);

      return;
    }

    if (!options.publicApiKey) {
      reject(new Error("[affirm-placement] Missing public-api-key."));

      return;
    }

    const environment =
      options.environment === "sandbox" ? "sandbox" : "production";

    bootstrapAffirm({
      publicApiKey: options.publicApiKey,
      scriptUrl: SCRIPT_URLS[environment],
      locale: options.locale || "en_US",
      countryCode: options.countryCode || "USA",
    });

    waitForAffirm(options.timeout).then(resolve).catch(reject);
  }).catch((error) => {
    affirmLoadPromise = null;
    throw error;
  });

  return affirmLoadPromise;
}

function requestAffirmRefresh(options) {
  if (refreshFrame !== null) {
    return;
  }

  refreshFrame = window.requestAnimationFrame(() => {
    refreshFrame = null;

    loadAffirm(options)
      .then((affirm) => {
        affirm.ui.ready(() => {
          affirm.ui.refresh();
        });
      })
      .catch((error) => {
        console.error(error);
      });
  });
}

class AffirmPlacement extends HTMLElement {
  static get observedAttributes() {
    return [
      "amount",
      "data-amount",
      "data-purchase-amount",
      "page-type",
      "promo-id",
      "affirm-type",
      "affirm-color",
      "category",
      "brand",
      "sku",
      "learn-more",
    ];
  }

  constructor() {
    super();

    this._placement = null;
    this._connected = false;
  }

  connectedCallback() {
    this._connected = true;

    this.ensurePlacement();
    this.syncAttributes();

    loadAffirm(this.getLoaderOptions())
      .then(() => {
        requestAffirmRefresh(this.getLoaderOptions());

        this.dispatchEvent(new CustomEvent("affirm-ready", { bubbles: true }));
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  disconnectedCallback() {
    this._connected = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this._connected || !this.isConnected) {
      return;
    }

    /*
     * 初始化配置不允许在 Affirm 加载后动态切换。
     */
    if (
      ["public-api-key", "environment", "locale", "country-code"].includes(name)
    ) {
      return;
    }

    this.ensurePlacement();
    this.syncAttributes();
    requestAffirmRefresh(this.getLoaderOptions());
  }

  get amount() {
    const value = this.getAmountValue();

    if (value === null) {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
  }

  set amount(value) {
    if (value === null || value === undefined || value === "") {
      this.removeAttribute("amount");
      return;
    }

    this.setAttribute("amount", String(value));
  }

  getLoaderOptions() {
    return {
      publicApiKey: this.getAttribute("public-api-key"),
      environment: this.getAttribute("environment") || "production",
      locale: this.getAttribute("locale") || "en_US",
      countryCode: this.getAttribute("country-code") || "USA",
      timeout: 15_000,
    };
  }

  ensurePlacement() {
    if (this._placement && this.contains(this._placement)) {
      return;
    }

    const placement = document.createElement("p");

    placement.className = "affirm-as-low-as";
    placement.dataset.affirmPlacementTarget = "";

    this.replaceChildren(placement);
    this._placement = placement;
  }

  syncAttributes() {
    this.syncAmount();

    for (const [source, target] of Object.entries(ATTRIBUTE_MAP)) {
      const value = this.getAttribute(source);

      if (value === null || value === "") {
        this._placement.removeAttribute(target);
      } else {
        this._placement.setAttribute(target, value);
      }
    }

    if (!this._placement.hasAttribute("data-page-type")) {
      this._placement.setAttribute("data-page-type", "product");
    }
  }

  syncAmount() {
    const rawAmount = this.getAmountValue();

    if (rawAmount === null || rawAmount.trim() === "") {
      this.hide();
      return;
    }

    const amount = Number(rawAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      this.hide();

      this.handleError(
        new TypeError(`[affirm-placement] Invalid amount: ${rawAmount}`),
      );

      return;
    }

    this._placement.hidden = false;
    this._placement.setAttribute("data-amount", String(Math.round(amount)));
  }

  getAmountValue() {
    return (
      this.getAttribute("amount")
      ?? this.getAttribute("data-purchase-amount")
      ?? this.getAttribute("data-amount")
    );
  }

  hide() {
    this._placement.hidden = true;
    this._placement.removeAttribute("data-amount");
  }

  refresh() {
    this.ensurePlacement();
    this.syncAttributes();
    requestAffirmRefresh(this.getLoaderOptions());
  }

  handleError(error) {
    console.error(error);

    this.dispatchEvent(
      new CustomEvent("affirm-error", { bubbles: true, detail: { error } }),
    );
  }
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, AffirmPlacement);
}